/**
 * Confidence -> marker style mapping — the visual core of the "fusion" concept: a single
 * unconfirmed report renders as a large, fuzzy, translucent blob; as corroborating reports
 * arrive it tightens, darkens, and sharpens into a small solid ring once officially declared.
 *
 * Radius is ordinal-by-status (pixels), not a true geodesic rendering of uncertainty_radius_m —
 * MapLibre's circle-radius has no native meters-at-latitude unit, and a real geodesic uncertainty
 * polygon is a reasonable follow-up (a filled Turf.js circle per feature) rather than a day-one
 * requirement. The pixel ordinal already carries the "tightening as confidence rises" signal.
 */
import type { ContactStatus } from '../../types/contact'

// Matches tailwind.config.ts `confidence.*` — kept as literal hex here because MapLibre paint
// expressions can't reference Tailwind's generated CSS custom properties.
export const STATUS_COLORS: Record<ContactStatus, string> = {
  unconfirmed: '#64748b',
  corroborated: '#0891b2',
  high_confidence: '#d97706',
  officially_declared: '#15803d',
}

// MapLibre expressions are a heterogeneous nested-array DSL — not worth modelling precisely
// here; the paint-property call sites already cast to `any` for maplibre-gl's own expression type.
type MatchExpr = unknown[]

function statusMatch(values: Record<ContactStatus, number>): MatchExpr {
  return [
    'match',
    ['get', 'status'],
    'unconfirmed', values.unconfirmed,
    'corroborated', values.corroborated,
    'high_confidence', values.high_confidence,
    'officially_declared', values.officially_declared,
    values.unconfirmed,
  ]
}

function statusColorMatch(): MatchExpr {
  return [
    'match',
    ['get', 'status'],
    'unconfirmed', STATUS_COLORS.unconfirmed,
    'corroborated', STATUS_COLORS.corroborated,
    'high_confidence', STATUS_COLORS.high_confidence,
    'officially_declared', STATUS_COLORS.officially_declared,
    STATUS_COLORS.unconfirmed,
  ]
}

// Shrinks circles further as the map zooms out, on top of the status-based sizing below — at
// country-wide zoom a cluster of contacts otherwise renders as a solid mass of same-size, always
// overlapping circles (same underlying bug CAP-alert-platform's AlertLayer hit and fixed with an
// analogous zoom-and-property pattern). MapLibre's style spec only allows a ["zoom"] expression as
// the sole top-level interpolate input — it can't be composed with e.g. ["*", ...] — so zoom must
// be the outer interpolate, with a status-based match as each stop's output.
const ZOOM_RADIUS_STOPS: [zoom: number, factor: number][] = [[3, 0.35], [5, 0.55], [8, 0.85], [11, 1]]

function zoomScaledStatusRadius(values: Record<ContactStatus, number>): MatchExpr {
  return [
    'interpolate', ['linear'], ['zoom'],
    ...ZOOM_RADIUS_STOPS.flatMap(([zoom, factor]) => [
      zoom,
      statusMatch({
        unconfirmed: values.unconfirmed * factor,
        corroborated: values.corroborated * factor,
        high_confidence: values.high_confidence * factor,
        officially_declared: values.officially_declared * factor,
      }),
    ]),
  ]
}

export const CIRCLE_RADIUS = zoomScaledStatusRadius({
  unconfirmed: 26,
  corroborated: 19,
  high_confidence: 15,
  officially_declared: 11,
})

export const CIRCLE_COLOR = statusColorMatch()

export const CIRCLE_OPACITY = statusMatch({
  unconfirmed: 0.12,
  corroborated: 0.28,
  high_confidence: 0.45,
  officially_declared: 0.75,
})

export const CIRCLE_BLUR = statusMatch({
  unconfirmed: 0.7,
  corroborated: 0.4,
  high_confidence: 0.15,
  officially_declared: 0,
})

export const CIRCLE_STROKE_WIDTH = statusMatch({
  unconfirmed: 1,
  corroborated: 1.5,
  high_confidence: 2,
  officially_declared: 2.5,
})

export const CIRCLE_STROKE_OPACITY = statusMatch({
  unconfirmed: 0.3,
  corroborated: 0.55,
  high_confidence: 0.8,
  officially_declared: 1,
})
